/**
 * Company attribution: the brand lockup and the statutory disclosure block.
 *
 * This mirrors backend/config/brand.py exactly. Two different things live here
 * and they are never merged into one line:
 *
 *   1. The LOCKUP, "GreenFlow, from Attavor". A quiet brand line in the pattern
 *      of "Claude by Anthropic" or "Instagram from Meta". No legal weight.
 *   2. The STATUTORY BLOCK. Legal name, place of registration and registration
 *      number, registered office, and where the privacy notice can be read.
 *
 * WHY EVERY LINE IS OPTIONAL
 *   A missing line is a smaller failure than a wrong one. A stale registration
 *   number on a screen a funder or registry reads is a false statement about a
 *   legal person. Each line is therefore omitted when its value is unset, and
 *   nothing is ever defaulted to a guess.
 *
 * WHY THE VALUES COME FROM THE ENVIRONMENT
 *   The same bundle serves local development, staging and production, and only
 *   production should assert the registry facts. Create React App inlines these
 *   at build time, so the deployment that builds the bundle decides what it
 *   claims. The authoritative values are the ones filed with the registry and
 *   recorded in STATE.md, section "Legal entity: Attavor Integrated Limited".
 */

/**
 * Read one build-time variable, treating blank as unset.
 *
 * A variable exported as an empty string means "I have no value for this", not
 * "render an empty line".
 *
 * @param {string|undefined} value Raw value from process.env.
 * @param {string} fallback Value to use when the variable is absent.
 * @returns {string} The trimmed value or the trimmed fallback.
 */
const read = (value, fallback = '') => String(value ?? fallback ?? '').trim();

export const BRAND_NAME = read(process.env.REACT_APP_BRAND_NAME, 'GreenFlow');
export const BRAND_OWNER_SHORT = read(process.env.REACT_APP_BRAND_OWNER_SHORT, 'Attavor');
export const BRAND_OWNER_LEGAL_NAME = read(
  process.env.REACT_APP_BRAND_OWNER_LEGAL_NAME,
  'Attavor Integrated Limited',
);

// No default. A wrong number is worse than no line.
export const COMPANY_REGISTRATION_NUMBER = read(process.env.REACT_APP_COMPANY_REGISTRATION_NUMBER);
export const COMPANY_REGISTRATION_COUNTRY = read(
  process.env.REACT_APP_COMPANY_REGISTRATION_COUNTRY,
  'Nigeria',
);
export const REGISTERED_OFFICE = read(process.env.REACT_APP_REGISTERED_OFFICE);
export const PRIVACY_NOTICE_URL = read(process.env.REACT_APP_PRIVACY_NOTICE_URL);

/**
 * The one-line brand lockup, for example "GreenFlow, from Attavor".
 *
 * When no owner short name is configured the product name is returned alone, so
 * a deployment that has not been told who owns the product says nothing about
 * ownership rather than rendering a dangling "GreenFlow, from".
 *
 * @returns {string} The lockup line.
 */
export function brandLockup() {
  if (!BRAND_OWNER_SHORT) return BRAND_NAME;
  return `${BRAND_NAME}, from ${BRAND_OWNER_SHORT}`;
}

/**
 * The statutory disclosure lines paired with their links.
 *
 * A renderer needs to know which line is a hyperlink without parsing it back
 * out of a formatted string.
 *
 * @returns {Array<{text: string, href: string}>} Lines in disclosure order.
 *   `href` is an empty string for anything that is not a link. A line whose
 *   value is unset is absent entirely.
 */
export function statutoryEntries() {
  const entries = [];

  if (BRAND_OWNER_LEGAL_NAME) {
    entries.push({ text: BRAND_OWNER_LEGAL_NAME, href: '' });
  }

  // The number is the identifying fact. The country on its own tells a reader
  // nothing they can look up, so it never appears as a line of its own.
  if (COMPANY_REGISTRATION_NUMBER) {
    entries.push({
      text: COMPANY_REGISTRATION_COUNTRY
        ? `Registered in ${COMPANY_REGISTRATION_COUNTRY}, registration number ${COMPANY_REGISTRATION_NUMBER}`
        : `Registration number ${COMPANY_REGISTRATION_NUMBER}`,
      href: '',
    });
  }

  if (REGISTERED_OFFICE) {
    entries.push({ text: `Registered office: ${REGISTERED_OFFICE}`, href: '' });
  }

  if (PRIVACY_NOTICE_URL) {
    entries.push({ text: 'Privacy notice', href: PRIVACY_NOTICE_URL });
  }

  return entries;
}

/**
 * The statutory disclosure as plain text lines.
 *
 * @returns {string[]} One string per line that has a value, in the order: legal
 *   name; place of registration and registration number; registered office;
 *   privacy notice. An empty array is valid and means nothing is configured.
 */
export function statutoryLines() {
  return statutoryEntries().map(({ text, href }) => (href ? `${text}: ${href}` : text));
}
